import { Component, OnInit, Renderer2 } from '@angular/core';
import { AsyncPipe, NgIf } from '@angular/common';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute, NavigationEnd, Params, Router, RouterModule, RouterOutlet } from '@angular/router';

import { BehaviorSubject, filter, from, Observable, Subject, Subscription, takeUntil, tap } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { LangChangeEvent, TranslateModule, TranslateService } from '@ngx-translate/core';

import { Language } from './shared/config/app.constants';
import { SafeDropLocation } from './shared/models/safedroplocation';
import { SafeDropLocationService } from './shared/services/safe-drop-location.service';
import { LocalizeRouter } from './shared/services/localize-router.service';
import { CustomLocalizePipe } from './shared/pipes/custom-localize.pipe';
import { HomeModuleModule } from './modular/home/home.module';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';

@Component({
  selector: 'cpc-app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterModule,
    TranslateModule,
    HomeModuleModule,
    NgIf,
    AsyncPipe,
    MatSnackBarModule,
    CustomLocalizePipe
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  isAuthorized$: BehaviorSubject<boolean>;
  safeDropLocation$: Observable<SafeDropLocation>;
  currentLang: Language;
  private routerSubscription: Subscription;
  private onDestroy$ = new Subject<void>();
  private routesWithNoHeaderAndSlimFooter: string[] = ['/login','/ouvertur-session','/ouverture-session'];

  constructor(
    private _oAuthService: OAuthService,
    public _translateService: TranslateService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _localize: LocalizeRouter,
    private _localizeRouteService: LocalizeRouterService,
    private _safeDropLocationService: SafeDropLocationService,
    private renderer: Renderer2
  ) {
    this.isAuthorized$ = new BehaviorSubject(false);
    _translateService.addLangs([Language.ENGLISH, Language.FRENCH]);
    _translateService.setDefaultLang(Language.ENGLISH);


    // this._route.queryParams.subscribe(
    //   (params: Params) => {
    //     this.translateBrowserLang()
    //   })
  }

  ngOnInit(): void {
    // Subscribe to OAuth service events to handle token reception
    // This occurs after a successful redirection from the identity server
    if (window.location.pathname.includes('/ouvertur-session')) {
      const newPath = window.location.pathname.replace(
        '/ouvertur-session','/ouverture-session');
        window.history.replaceState({},'',newPath + window.location.search + window.location.hash);
    }
    this._oAuthService.events.pipe(
      tap(event => console.log(event)),
      filter(event => event.type === 'token_received') // Filter for token received events
    ).subscribe(() => {
      this.isAuthorized$.next(true); // Update authorization status when token is received
    });

    // this.checkLanguage();

    // Load the discovery document and attempt login
    from(this._oAuthService.loadDiscoveryDocumentAndTryLogin()).subscribe(
      () => {
        // Check if a valid access token exists
        if (!this._oAuthService.hasValidAccessToken()) {
          this._oAuthService.initLoginFlow(); // Initiate login flow if no valid token
        } else {
          this.isAuthorized$.next(true); // Set authorization status to true if token is valid
        }
      });

    // Subscribe to language change events to handle language updates
    this.subscribeToLanguageChangeEvent();

    // Subscribe to route parameters to handle URL query params
    this.subscribeToRouteParams();

    // Fetch the safe drop location observable from the service
    this.safeDropLocation$ = this._safeDropLocationService.getSafeDropLocation();

    // Handle initial load (refresh)
    this.handleLayoutBasedOnUrl(this._router.url);

    // Subscribe once to NavigationEnd events
    this._router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.onDestroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.handleLayoutBasedOnUrl(event.urlAfterRedirects);
      });
  }



  private subscribeToLanguageChangeEvent(): void {
    // Listen for language change events from the translation service
    this._translateService.onLangChange.subscribe(
      (event: LangChangeEvent) => {

        this._translateService.use(event.lang); // Change the language in the translation service

        // Set current language based on event; default to English if not French
        this.currentLang = event.lang === Language.FRENCH ? Language.FRENCH : Language.ENGLISH;
        this.syncLanguageState(this.currentLang);

        // Update safe drop location observable based on new language
        this.safeDropLocation$ = this._safeDropLocationService.onLangChange(event.lang);
      }
    );
  }

  private syncLanguageState(lang: string): void {
    localStorage.setItem('locale', lang);
    document.documentElement.setAttribute('lang', lang);

    const title = lang === Language.FRENCH ? 'Profil | Postes Canada' : 'Profile | Canada Post';

    try {
      window.parent.document.title = title;
    }
    catch {
      document.title = title;
    }
  }

  private subscribeToRouteParams(): void {
    // Subscribe to query parameters in the current route
    this._route.queryParams.subscribe(
      (params: Params) => {


        if (!Object.keys(params).length) {
          // If no parameters, translate browser language and exit early
          this.translateBrowserLang();
          return;
        }

        // Log search route if present in query parameters
        // if (params[this._localize.route('search')]) {
        // }

        // Use language specified in query parameters or fallback to browser language
        if (params['lang']) {
          this._translateService.use(params['lang']);
        } else if (params['langue']) {
          this._translateService.use(params['langue']);
        } else {
          this.translateBrowserLang(); // Fallback to browser language if no lang param found
        }
      }
    );
  }

  private translateBrowserLang(): void {
    // Get the browser's preferred language and set it in the translation service
    // const browserLang = this._translateService.getBrowserLang();


    // // Use English as default if browser language is not English or French
    // this._translateService.use(browserLang?.match(/en|fr/) ? browserLang : Language.ENGLISH);


    let index = window.location.href.indexOf('/lfe-cap/');
    let currentLang, newURL = '';
    let lastLang;

    sessionStorage.setItem('route', localStorage.getItem('locale') ?? 'en');
    if (window.location.href.substr(index + 9, 2) == "en" || window.location.href.substr(index + 9, 2) == "fr") {
      currentLang = window.location.href.substr(index + 9, 2);
      this._translateService.use(currentLang);
      localStorage.setItem('locale', currentLang);
      lastLang = sessionStorage.getItem('route');
      sessionStorage.setItem('route', currentLang);
    }
    //TODO: This has to be removed after redirect link updates
    else if (window.location.href.substr(index + 27, 2) == "en" || window.location.href.substr(index + 27, 2) == "fr") {
      newURL = window.location.href;
      newURL = newURL.replace('index.html?lang=', '');
      currentLang = newURL.substr(index + 9, 2);
      this._translateService.use(currentLang);
      localStorage.setItem('locale', currentLang);
      lastLang = sessionStorage.getItem('route');
      sessionStorage.setItem('route', currentLang);
      if (lastLang && (currentLang == lastLang)) {
        let translatePath = this._localizeRouteService.translateRoute(newURL);
        window.location.href = String(translatePath);
      }
    }
    if (lastLang && (currentLang != lastLang)) {
      let translatePath = newURL === '' ? this._localizeRouteService.translateRoute(window.location.href) : this._localizeRouteService.translateRoute(newURL);
      //window.history.pushState({ prevUrl: window.location.href }, null, String(translatePath));
      window.location.href = String(translatePath);
    }


  }

  private checkLanguage(): void {
    const langParam = localStorage.getItem('locale') ?? 'en';
    if (langParam == 'en' || langParam == 'fr') {
      this._translateService.use(langParam)
    } else {
      this._translateService.use(Language.ENGLISH)
    }
    this.translateBrowserLang()

  }

  public changeLanguage(): void {
    // Toggle between English and French based on current language setting
    const lang = this._translateService.currentLang === Language.ENGLISH ? { langue: Language.FRENCH } : { lang: Language.ENGLISH };

    const url = `/home-standalone`; // Define target URL for navigation

    // Navigate with updated query parameters and reload the page afterward
    this._router.navigate(this._localize.route([url]),
      { queryParams: lang })
      .then(() => {
        window.location.reload(); // Reload the page to apply changes immediately
      });
  }

  public navigate(): void {
    // Navigate to home-standalone with specific query parameters and fragment
    this._router.navigate(
      this._localize.route(['home-standalone']),
      {
        queryParams: this._localize.route({ 'search': 'test', 'key': 2 }), // Set query parameters for navigation
        fragment: this._localize.route('fragment') // Set fragment for navigation
      }
    )
  }

  public navigateUrl(): void {
    // Navigate directly to a specific URL with query parameters and fragment included
    this._router.navigateByUrl(
      this._localize.route('/home-modular/banner?search=test&key=2#fragment')
    );
  }

  handleLayoutBasedOnUrl(url: string) {
    document.documentElement.classList.remove('auth-layout');
    if (this.routesWithNoHeaderAndSlimFooter.some(route => url.includes(route))) {
      this.removeHeaderAndFooter();
    } else {
      this.revertHeaderAndFooterToDefault();
    }
  }

  removeHeaderAndFooter() {
    const footer = document.querySelector('cpc-footer');
    const header = document.querySelector('cpc-header');

    if (footer) {
      this.renderer.addClass(footer, 'slim-footer');
      this.renderer.removeClass(footer, 'cpc-component');
    }
    if (header) {
      this.renderer.setStyle(header, 'display', 'none');
      this.renderer.removeClass(header, 'cpc-component');
    }
  }

  revertHeaderAndFooterToDefault() {
    const footer = document.querySelector('cpc-footer');
    const header = document.querySelector('cpc-header');

    if (footer) {
      this.renderer.removeClass(footer, 'slim-footer');
      this.renderer.removeClass(footer, 'cpc-component');
    }
    if (header) {
      this.renderer.removeStyle(header, 'display');
       this.renderer.removeClass(header, 'cpc-component');
    }
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.revertHeaderAndFooterToDefault();
  }
}
